import { forwardRef } from 'react';

// We are creating the wrappers here to be used everywhere else
// eslint-disable-next-line no-restricted-imports
import {
  Link as OriginalLink,
  LinkProps,
  Navigate as OriginalNavigate,
  NavLink as OriginalNavLink,
  NavLinkProps,
  useParams,
  NavigateProps as OriginalNavigateProps,
  useNavigate as useOriginalNavigate,
  NavigateOptions,
  useLocation as useOriginalLocation,
  To,
} from 'react-router-dom';
import { useCallback } from 'react';

const resolvePath = (to: LinkProps['to'], brand?: string) => {
  if (!brand) return to;
  if (typeof to === 'string' && to.startsWith('/')) {
    return `/${brand}${to}`;
  }
  if (typeof to === 'object' && to.pathname?.startsWith('/')) {
    return { ...to, pathname: `/${brand}${to.pathname}` };
  }
  return to;
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { name } = useParams();
  return <OriginalLink {...props} to={resolvePath(props.to, name)} ref={ref} />;
});

Link.displayName = 'Link';

const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => {
  const { name } = useParams();
  return <OriginalNavLink {...props} to={resolvePath(props.to, name)} ref={ref} />;
});

NavLink.displayName = 'NavLink';

type NavigateProps = OriginalNavigateProps & {
  doNotResolveBrandingPath?: boolean;
};

const Navigate = ({ doNotResolveBrandingPath, ...props }: NavigateProps) => {
  const { name } = useParams();
  return (
    <OriginalNavigate
      {...props}
      to={doNotResolveBrandingPath ? props.to : resolvePath(props.to, name)}
    />
  );
};

interface BrandedNavigateOptions extends NavigateOptions {
  doNotResolveBrandingPath?: boolean;
}

function useNavigate() {
  const navigate = useOriginalNavigate();
  const { name } = useParams();

  const brandNavigate = useCallback(
    (toOrDelta: To | number, options: BrandedNavigateOptions = {}) => {
      if (typeof toOrDelta === 'number') {
        return navigate(toOrDelta);
      }
      const { doNotResolveBrandingPath, ...rest } = options;
      return navigate(doNotResolveBrandingPath ? toOrDelta : resolvePath(toOrDelta, name), rest);
    },
    [navigate, name],
  );

  return brandNavigate;
}

type NavigateFunction = ReturnType<typeof useNavigate>;

function useLocation() {
  const { name } = useParams();
  const location = useOriginalLocation();

  let pathnameWithoutBrand = location.pathname;

  if (name && location.pathname.startsWith(`/${name}`)) {
    pathnameWithoutBrand = location.pathname.replace(`/${name}`, '');
    if (pathnameWithoutBrand === '') {
      pathnameWithoutBrand = '/';
    }
  }

  return {
    ...location,
    pathname: pathnameWithoutBrand,
    pathnameWithBrand: location.pathname,
  };
}

// We are exporting the original react-router-dom functions and augmenting some of them
// to be used everywhere else across the application
// eslint-disable-next-line no-restricted-imports
export * from 'react-router-dom';
export {
  Link,
  NavLink,
  Navigate,
  type NavigateProps,
  useNavigate,
  useLocation,
  type NavigateFunction,
};
